import { Link, useParams, useSearchParams } from "react-router-dom";

import { StatusBadge } from "../components/Badges";
import { BarList } from "../components/BarList";
import { DataTable } from "../components/DataTable";
import { MonthBars } from "../components/MonthBars";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import type { DocStatus } from "../lib/api";
import { day, money, num, pct, usd } from "../lib/format";
import { useApi } from "../lib/useApi";
import { CustomerTable, InvoiceTable, type InvoiceRow } from "./Sales";

export function Customers() {
  const [params, setParams] = useSearchParams();
  const channel = params.get("channel") ?? "";
  const { data, error, reload } = useApi<Parameters<typeof CustomerTable>[0]["rows"]>(`/customers${channel ? `?channel=${channel}` : ""}`);
  const channels = useApi<{ code: string; name: string }[]>("/channels");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  return (
    <>
      <PageHeader title="Customers" sub="Everyone who bought, from the sales reports. Linked to the address book where a document names them." />
      <Card actions={
        <select aria-label="Channel" value={channel} onChange={(e) => {
          const n = new URLSearchParams(params); if (e.target.value) n.set("channel", e.target.value); else n.delete("channel"); setParams(n, { replace: true });
        }}>
          <option value="">All channels</option>
          {(channels.data ?? []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>}>
        {data ? <CustomerTable rows={data} /> : <Loading />}
      </Card>
    </>
  );
}

interface CustDetail {
  id: number; name: string; ext_code: string | null; channel: string | null; channel_name: string | null;
  summary: { amount_usd: number | null; cases: number | null; invoices: number; last_sale: string | null; share: number } | null;
  monthly: { month: string; amount_usd: number }[];
  by_sku: { sku: string; name: string; cases: number }[];
  party: { id: number; name: string } | null;
  documents: { id: number; doc_type: string; number: string; status: DocStatus; date: string | null; total: number }[];
}

export function CustomerDetail() {
  const { id } = useParams();
  const { data, error, reload } = useApi<CustDetail>(`/customers/${id}`);
  const inv = useApi<InvoiceRow[]>(`/sales/invoices?customer_id=${id}`);
  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "invoices", label: "Invoices", count: inv.data?.length },
    { id: "products", label: "Products", count: data?.by_sku.length },
    { id: "documents", label: "Documents", count: data?.documents.length },
  ];
  const [tab, setTab] = useTab(tabs);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const s = data.summary;
  return (
    <>
      <PageHeader crumbs={[{ label: "Customers", to: "/customers" }]} title={data.name} sub={data.channel_name} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "overview" && (
        <div className="two-up">
          <Card title="Summary">
            <Facts items={[["Channel", data.channel_name], ["Customer code", data.ext_code], ["Revenue", usd(s?.amount_usd)],
              ["Share of revenue", pct(s?.share)], ["Cases", num(s?.cases)], ["Invoices", num(s?.invoices)], ["Last sale", day(s?.last_sale)],
              ["Address book", data.party ? <Link to={`/settings/address-book/${data.party.id}`}>{data.party.name}</Link> : "not linked"]]} />
          </Card>
          <Card title="Revenue by month">
            <MonthBars title="Revenue" points={data.monthly.map((m) => ({ month: m.month, value: m.amount_usd, label: usd(m.amount_usd) }))} />
          </Card>
        </div>
      )}
      {tab === "invoices" && <Card>{inv.data ? <InvoiceTable rows={inv.data} /> : <Loading />}</Card>}
      {tab === "products" && (
        <Card title="Cases by product">
          <BarList bars={data.by_sku.map((p) => ({ key: p.sku, label: p.name, value: p.cases, valueLabel: `${num(p.cases)} cs`, href: `/products/${p.sku}` }))} />
        </Card>
      )}
      {tab === "documents" && (
        <Card>
          <DataTable rows={data.documents} rowKey={(d) => d.id} href={(d) => `/documents/${d.id}`} filterPlaceholder={false}
            empty="No documents linked to this customer." columns={[
              { key: "number", label: "Number", render: (d) => <Link to={`/documents/${d.id}`}>{d.number}</Link> }, { key: "status", label: "Status", render: (d) => <StatusBadge status={d.status} docType={d.doc_type} /> },
              { key: "date", label: "Date", render: (d) => day(d.date) }, { key: "total", label: "Total", numeric: true, render: (d) => money(d.total) },
            ]} />
        </Card>
      )}
    </>
  );
}
